/**
 * Msg 592 · Phase A follow-up — Expanded Lucide icon set for
 * user-defined Atlas categories (~150+ icons).
 *
 * Names are canonical `lucide-react` exports. Additive only — every
 * icon that was in the pre-Msg-592 map is still here so existing
 * `atlas_categories.icon_name` values keep resolving.
 *
 * Adding a new icon: append the (Name, Component) pair below. Names
 * must match the lucide-react export exactly so the admin picker
 * searches by string.
 */
import {
  // ── Travel / transport
  Anchor, Ship, Sailboat, Plane, PlaneTakeoff, PlaneLanding,
  Car, Bus, Train, TrainFront, Bike, Truck, Rocket, CarTaxiFront,
  Fuel, Luggage, Backpack, Route, Waypoints, MapPinned,
  // ── Lodging / buildings
  Hotel, Bed, BedDouble, House, Home, Building, Building2, Warehouse,
  Tent, Trees, TreePine, TreeDeciduous, Castle, Landmark, Church,
  // ── Destinations / geo / weather
  MapPin, Map, Globe, Globe2, Compass, Mountain, MountainSnow,
  Sun, Sunrise, Sunset, Moon, Cloud, Cloudy, CloudRain, CloudSun,
  CloudMoon, Snowflake, Waves, Fish, Palmtree, Flower, Flower2,
  Rainbow, Droplet, Droplets, Wind, ThermometerSun, ThermometerSnowflake,
  // ── Food / drink
  Utensils, UtensilsCrossed, Coffee, Wine, Beer, Martini, GlassWater,
  Pizza, IceCream, Cake, Cookie, Salad, Sandwich, Croissant,
  Cherry, Grape, Egg, Beef, Milk, FlaskConical,
  // ── Activities / leisure / entertainment
  Ticket, Camera, Music, Music2, Music3, Music4, Headphones, Radio,
  Film, Popcorn, Gamepad, Gamepad2, Trophy, Award, Medal, PartyPopper,
  Sparkles, Gift, Heart, Flame,
  // ── Nature / wildlife
  Bird, Dog, Cat, Rabbit, Squirrel, Turtle, Bug, Leaf, Sprout,
  // ── Utilities / abstract
  Folder, FolderOpen, Book, BookOpen, Bookmark, FileText, ClipboardList,
  Tag, Star, Circle, Square, Diamond, Hexagon, Triangle, Zap, Bolt,
  Feather, Cross,
  // ── Commerce
  ShoppingBag, ShoppingCart, DollarSign, CreditCard, Wallet, Receipt,
  PiggyBank,
  // ── People / family
  User, Users, UserRound, Baby, PersonStanding,
  // ── Misc travel
  Umbrella, LifeBuoy, Volleyball, Dumbbell,
  // ── Msg 593 supplementary — additive only. Verified against
  // `lucide-react` at Msg 593 write-time; Volcano/Buildings/Skis/
  // Butterfly/Passport/Taxi/Fire aren't in the package and were
  // skipped silently.
  Shell, TowerControl, Binoculars, Footprints, TrafficCone,
  CloudSnow, Crown, Briefcase, IdCard, Thermometer,
  TreePalm, Package,
  // ── Msg 594 — accessibility + broader coverage batch. Additive
  // only; 6 names skipped silently (Wheelchair, WalkingPerson, Hands,
  // CrossCircle, FirstAid, Contract are not in lucide-react at
  // Msg-594 write-time).
  Accessibility, Ear, EarOff, Eye, EyeOff, Hand, HandHelping,
  HeartHandshake, HeartPulse, Stethoscope, Pill, Cast, Bandage,
  Activity, PawPrint, Syringe, Dna, Brain, Bone, UsersRound,
  Languages, Speech, MessageCircle, MessageSquare, Mic, MicOff,
  Volume, Volume1, Volume2, VolumeX,
  Euro, PoundSterling, IndianRupee, JapaneseYen, RussianRuble,
  SwissFranc, Bitcoin, Coins, Banknote,
  Shield, ShieldCheck, ShieldAlert, Lock, Unlock, Key, KeyRound,
  Fingerprint, AlertTriangle, AlertCircle, Info, HelpCircle,
  Wifi, WifiOff, Signal, SignalHigh, SignalLow, Bluetooth,
  Battery, BatteryLow, Phone, PhoneCall, Smartphone, Tablet,
  Laptop, Monitor, Video, VideoOff,
  Clock, Timer, Watch, Calendar, CalendarDays, CalendarClock,
  CalendarCheck, CalendarX, Hourglass,
  FileCheck, FileWarning, FileX, ScrollText, Stamp, PenTool,
  Signature, Scale, Gavel,
  Flag, GraduationCap,
  Recycle, Store, ShoppingBasket, PackageCheck, PackageOpen, Tags,
  Smile, Frown, Meh, Laugh, Angry, ThumbsUp, ThumbsDown, HeartCrack,
  // ── Msg 607 · Expansion batch (263 → ~500). Additive only.
  // Every name below has been verified against `lucide-react`
  // at write-time. Missing user-requested icons (Yacht, PortholeIcon,
  // DockPier, Crane, Snorkel, Sunglasses, Sunscreen, FlipFlops,
  // Coconut, Bread, Bacon, Chocolate, Corn, Soup, TeapotIcon,
  // Helicopter, FireTruck, Scooter, Motorcycle, Skateboard,
  // Rollerskate, Skiing, Snowmobile, Suitcase, Yoga, Pilates,
  // Massage, Sofa, Fridge, Kid, Family, Group, Gem, Wand,
  // Balloon, Champagne, Achievement, Braille, WindSpeed) are
  // skipped silently — they're not in the lucide-react package.
  //   Music / audio / entertainment
  Piano, Guitar, Drum, Speaker, AudioLines, AudioWaveform, Podcast,
  Clapperboard, BookmarkPlus, Play, Pause, Disc, Disc3,
  Library, Newspaper, Puzzle, Dice1, Dice3, Dice5, MedalIcon,
  Palette, Brush, Paintbrush,
  //   Cruise / ships / nautical — Ferry, Yacht etc skipped (not in lucide)
  Radar, Bath, ShowerHead, Sofa,
  //   Adventure / outdoors
  Snail, Rat, Worm, Origami,
  //   Weather (expanded)
  CloudDrizzle, CloudFog, CloudHail, CloudLightning, CloudMoonRain,
  CloudSunRain, CloudRainWind, CloudOff,
  //   Food / drink (expanded)
  Apple, Carrot, Nut, Popsicle, Donut, CupSoda, Wheat, HandPlatter,
  //   Transport (expanded)
  CarFront, TramFront, Ambulance, Navigation, NavigationOff,
  ParkingSquare, ParkingCircle, TicketCheck, TicketPercent,
  //   Money (expanded)
  CircleDollarSign, HandCoins, ReceiptText,
  //   Comms (expanded)
  PhoneMissed, PhoneOff, PhoneOutgoing, PhoneIncoming, MessagesSquare,
  MailOpen, MailPlus, Mails, Send, Inbox, Bell, BellOff, BellRing,
  Megaphone, Rss,
  //   Docs / admin (expanded)
  Files, FilePlus, FileMinus, ClipboardCheck, Clipboard, Contact,
  ScrollIcon, ArchiveIcon, ArchiveRestore,
  //   UI / generic (expanded)
  StarHalf, ThumbsDown as ThumbsDownAlt, Bookmark as BookmarkAlt,
  Pin, PinOff, X, Check, Plus, Minus, Search, SlidersHorizontal,
  Filter, Sparkle, MoreHorizontal, MoreVertical, ArrowUp, ArrowDown,
  ArrowLeft, ArrowRight, ChevronUp, ChevronDown,
  //   Time / calendar (expanded)
  AlarmClock, AlarmClockOff, CalendarPlus, CalendarMinus, CalendarRange,
  CalendarSearch,
  //   Lodging / house (expanded)
  DoorOpen, DoorClosed, Lamp, WashingMachine,
  //   ── Business / analytics
  BarChart, BarChart2, BarChart3, LineChart, PieChart, TrendingUp,
  TrendingDown, Layers, Layers2, Layers3,
  //   ── Accommodations
  Boxes, Container,
  //   ── People / demographics
  User2, UserCheck, UserPlus, UserMinus, UserX,
  //   ── Misc
  Gem,
  //   ── Msg 607 · Part 2 · another verified batch (50 icons)
  ChefHat, Fence, StickyNote, NotebookPen, Notebook, NotebookText,
  PencilRuler, Ruler, Highlighter, Bold, Italic, Underline,
  BatteryCharging, BatteryFull, BatteryMedium, Plug, PlugZap, Power,
  Lightbulb, Tv, Tv2, Airplay, GitBranch, GitCommit, GitFork, Github,
  Chrome, MousePointer, QrCode, Barcode, ScanLine, Scan, Focus,
  Aperture, Image, ImagePlus, ImageDown, PackagePlus, PackageMinus,
  PackageSearch, PackageX, Pipette, TestTube, Beaker, Atom, Sigma,
  Anvil, Axe, Sword, Swords, FlaskRound, Milestone,
  //   ── Msg 607 · Part 3 · final push toward 500 (~65 icons)
  Wallet2, School, Hospital, SunMedium, SunDim, MoonStar, AlertOctagon,
  Save, Trash, Trash2, Undo, Undo2, Redo, Redo2, ClipboardCopy, Share,
  Share2, ExternalLink, Link2, Unlink, BookMarked, BookText, Users2,
  Target, Crosshair, Locate, LocateFixed, Antenna, CircleUser,
  CircleUserRound, CircleAlert, CircleCheck, CircleX, SmilePlus,
  FlagTriangleRight, FlagTriangleLeft, TentTree, Gauge,
  MapIcon, Split, Merge, ArrowLeftRight, ArrowUpDown, SortAsc, SortDesc,
  Rows, Columns, Grid3x3, LayoutGrid, LayoutList, LayoutDashboard,
  Kanban, KanbanSquare, ListChecks, ListTodo, ListPlus, ListMinus,
  ListX, SlidersVertical, Wrench, Hammer, Cog, ArrowUpRight,
  ArrowDownRight, ArrowUpLeft, ArrowDownLeft,
} from "lucide-react";

// Msg 595 — custom SVG icons that Lucide doesn't ship. Same visual
// signature (24×24, 2-stroke, currentColor) so they slot into the
// picker grid and tree rows identically.
import { Beach, Wheelchair, Passport, Volcano } from "./topicIconsCustom";


// Curated Lucide subset ordered by category so the picker grid feels
// scanned rather than random. 150+ entries.
export const CURATED_ICONS = [
  // ── Travel / transport
  { name: "Anchor",         cmp: Anchor },
  { name: "Ship",           cmp: Ship },
  { name: "Sailboat",       cmp: Sailboat },
  { name: "Plane",          cmp: Plane },
  { name: "PlaneTakeoff",   cmp: PlaneTakeoff },
  { name: "PlaneLanding",   cmp: PlaneLanding },
  { name: "Car",            cmp: Car },
  { name: "CarTaxiFront",   cmp: CarTaxiFront },
  { name: "Bus",            cmp: Bus },
  { name: "Train",          cmp: Train },
  { name: "TrainFront",     cmp: TrainFront },
  { name: "Bike",           cmp: Bike },
  { name: "Truck",          cmp: Truck },
  { name: "Rocket",         cmp: Rocket },
  { name: "Fuel",           cmp: Fuel },
  { name: "Luggage",        cmp: Luggage },
  { name: "Backpack",       cmp: Backpack },
  { name: "Route",          cmp: Route },
  { name: "Waypoints",      cmp: Waypoints },
  { name: "MapPinned",      cmp: MapPinned },

  // ── Lodging
  { name: "Hotel",          cmp: Hotel },
  { name: "Bed",            cmp: Bed },
  { name: "BedDouble",      cmp: BedDouble },
  { name: "House",          cmp: House },
  { name: "Home",           cmp: Home },
  { name: "Building",       cmp: Building },
  { name: "Building2",      cmp: Building2 },
  { name: "Warehouse",      cmp: Warehouse },
  { name: "Tent",           cmp: Tent },
  { name: "Trees",          cmp: Trees },
  { name: "TreePine",       cmp: TreePine },
  { name: "TreeDeciduous",  cmp: TreeDeciduous },
  { name: "Castle",         cmp: Castle },
  { name: "Landmark",       cmp: Landmark },
  { name: "Church",         cmp: Church },

  // ── Destinations / geo
  { name: "MapPin",         cmp: MapPin },
  { name: "Map",            cmp: Map },
  { name: "Globe",          cmp: Globe },
  { name: "Globe2",         cmp: Globe2 },
  { name: "Compass",        cmp: Compass },
  { name: "Mountain",       cmp: Mountain },
  { name: "MountainSnow",   cmp: MountainSnow },
  { name: "Palmtree",       cmp: Palmtree },
  { name: "Flower",         cmp: Flower },
  { name: "Flower2",        cmp: Flower2 },

  // ── Weather / season
  { name: "Sun",             cmp: Sun },
  { name: "Sunrise",         cmp: Sunrise },
  { name: "Sunset",          cmp: Sunset },
  { name: "Moon",            cmp: Moon },
  { name: "Cloud",           cmp: Cloud },
  { name: "Cloudy",          cmp: Cloudy },
  { name: "CloudRain",       cmp: CloudRain },
  { name: "CloudSun",        cmp: CloudSun },
  { name: "CloudMoon",       cmp: CloudMoon },
  { name: "Snowflake",       cmp: Snowflake },
  { name: "Waves",           cmp: Waves },
  { name: "Rainbow",         cmp: Rainbow },
  { name: "Droplet",         cmp: Droplet },
  { name: "Droplets",        cmp: Droplets },
  { name: "Wind",            cmp: Wind },
  { name: "ThermometerSun",  cmp: ThermometerSun },
  { name: "ThermometerSnowflake", cmp: ThermometerSnowflake },
  { name: "Umbrella",        cmp: Umbrella },

  // ── Food / drink
  { name: "Utensils",       cmp: Utensils },
  { name: "UtensilsCrossed", cmp: UtensilsCrossed },
  { name: "Coffee",         cmp: Coffee },
  { name: "Wine",           cmp: Wine },
  { name: "Beer",           cmp: Beer },
  { name: "Martini",        cmp: Martini },
  { name: "GlassWater",     cmp: GlassWater },
  { name: "Pizza",          cmp: Pizza },
  { name: "IceCream",       cmp: IceCream },
  { name: "Cake",           cmp: Cake },
  { name: "Cookie",         cmp: Cookie },
  { name: "Salad",          cmp: Salad },
  { name: "Sandwich",       cmp: Sandwich },
  { name: "Croissant",      cmp: Croissant },
  { name: "Cherry",         cmp: Cherry },
  { name: "Grape",          cmp: Grape },
  { name: "Egg",            cmp: Egg },
  { name: "Beef",           cmp: Beef },
  { name: "Milk",           cmp: Milk },
  { name: "FlaskConical",   cmp: FlaskConical },

  // ── Activities / leisure / entertainment
  { name: "Ticket",         cmp: Ticket },
  { name: "Camera",         cmp: Camera },
  { name: "Music",          cmp: Music },
  { name: "Music2",         cmp: Music2 },
  { name: "Music3",         cmp: Music3 },
  { name: "Music4",         cmp: Music4 },
  { name: "Headphones",     cmp: Headphones },
  { name: "Radio",          cmp: Radio },
  { name: "Film",           cmp: Film },
  { name: "Popcorn",        cmp: Popcorn },
  { name: "Gamepad",        cmp: Gamepad },
  { name: "Gamepad2",       cmp: Gamepad2 },
  { name: "Trophy",         cmp: Trophy },
  { name: "Award",          cmp: Award },
  { name: "Medal",          cmp: Medal },
  { name: "PartyPopper",    cmp: PartyPopper },
  { name: "Sparkles",       cmp: Sparkles },
  { name: "Gift",           cmp: Gift },
  { name: "Heart",          cmp: Heart },
  { name: "Flame",          cmp: Flame },
  { name: "Volleyball",     cmp: Volleyball },
  { name: "Dumbbell",       cmp: Dumbbell },
  { name: "LifeBuoy",       cmp: LifeBuoy },

  // ── Nature / wildlife
  { name: "Fish",           cmp: Fish },
  { name: "Bird",           cmp: Bird },
  { name: "Dog",            cmp: Dog },
  { name: "Cat",            cmp: Cat },
  { name: "Rabbit",         cmp: Rabbit },
  { name: "Squirrel",       cmp: Squirrel },
  { name: "Turtle",         cmp: Turtle },
  { name: "Bug",            cmp: Bug },
  { name: "Leaf",           cmp: Leaf },
  { name: "Sprout",         cmp: Sprout },

  // ── Utilities / abstract
  { name: "Folder",         cmp: Folder },
  { name: "FolderOpen",     cmp: FolderOpen },
  { name: "Book",           cmp: Book },
  { name: "BookOpen",       cmp: BookOpen },
  { name: "Bookmark",       cmp: Bookmark },
  { name: "FileText",       cmp: FileText },
  { name: "ClipboardList",  cmp: ClipboardList },
  { name: "Tag",            cmp: Tag },
  { name: "Star",           cmp: Star },
  { name: "Circle",         cmp: Circle },
  { name: "Square",         cmp: Square },
  { name: "Diamond",        cmp: Diamond },
  { name: "Hexagon",        cmp: Hexagon },
  { name: "Triangle",       cmp: Triangle },
  { name: "Zap",            cmp: Zap },
  { name: "Bolt",           cmp: Bolt },
  { name: "Feather",        cmp: Feather },
  { name: "Cross",          cmp: Cross },

  // ── Commerce
  { name: "ShoppingBag",    cmp: ShoppingBag },
  { name: "ShoppingCart",   cmp: ShoppingCart },
  { name: "DollarSign",     cmp: DollarSign },
  { name: "CreditCard",     cmp: CreditCard },
  { name: "Wallet",         cmp: Wallet },
  { name: "Receipt",        cmp: Receipt },
  { name: "PiggyBank",      cmp: PiggyBank },

  // ── People / family
  { name: "User",           cmp: User },
  { name: "Users",          cmp: Users },
  { name: "UserRound",      cmp: UserRound },
  { name: "Baby",           cmp: Baby },
  { name: "PersonStanding", cmp: PersonStanding },

  // ── Msg 593 supplementary travel icons. Additive only — surfaced
  // via the picker's search box (Shell, Crown, Rainbow duplicates
  // pruned).
  { name: "Shell",          cmp: Shell },
  { name: "TowerControl",   cmp: TowerControl },
  { name: "Binoculars",     cmp: Binoculars },
  { name: "Footprints",     cmp: Footprints },
  { name: "TrafficCone",    cmp: TrafficCone },
  { name: "CloudSnow",      cmp: CloudSnow },
  { name: "Crown",          cmp: Crown },
  { name: "Briefcase",      cmp: Briefcase },
  { name: "IdCard",         cmp: IdCard },
  { name: "Thermometer",    cmp: Thermometer },
  { name: "TreePalm",       cmp: TreePalm },
  { name: "Package",        cmp: Package },

  // ── Msg 594 — accessibility + broader coverage. Grouped for
  // scannability but the picker's search box is the primary
  // discovery path.
  //   Accessibility & health/medical
  { name: "Accessibility",  cmp: Accessibility },
  { name: "Ear",            cmp: Ear },
  { name: "EarOff",         cmp: EarOff },
  { name: "Eye",            cmp: Eye },
  { name: "EyeOff",         cmp: EyeOff },
  { name: "Hand",           cmp: Hand },
  { name: "HandHelping",    cmp: HandHelping },
  { name: "HeartHandshake", cmp: HeartHandshake },
  { name: "HeartPulse",     cmp: HeartPulse },
  { name: "HeartCrack",     cmp: HeartCrack },
  { name: "Stethoscope",    cmp: Stethoscope },
  { name: "Pill",           cmp: Pill },
  { name: "Syringe",        cmp: Syringe },
  { name: "Cast",           cmp: Cast },
  { name: "Bandage",        cmp: Bandage },
  { name: "Activity",       cmp: Activity },
  { name: "Dna",            cmp: Dna },
  { name: "Brain",          cmp: Brain },
  { name: "Bone",           cmp: Bone },
  { name: "PawPrint",       cmp: PawPrint },
  { name: "UsersRound",     cmp: UsersRound },
  //   Communication / language
  { name: "Languages",      cmp: Languages },
  { name: "Speech",         cmp: Speech },
  { name: "MessageCircle",  cmp: MessageCircle },
  { name: "MessageSquare",  cmp: MessageSquare },
  { name: "Mic",            cmp: Mic },
  { name: "MicOff",         cmp: MicOff },
  { name: "Volume",         cmp: Volume },
  { name: "Volume1",        cmp: Volume1 },
  { name: "Volume2",        cmp: Volume2 },
  { name: "VolumeX",        cmp: VolumeX },
  //   Currency
  { name: "Euro",           cmp: Euro },
  { name: "PoundSterling",  cmp: PoundSterling },
  { name: "IndianRupee",    cmp: IndianRupee },
  { name: "JapaneseYen",    cmp: JapaneseYen },
  { name: "RussianRuble",   cmp: RussianRuble },
  { name: "SwissFranc",     cmp: SwissFranc },
  { name: "Bitcoin",        cmp: Bitcoin },
  { name: "Coins",          cmp: Coins },
  { name: "Banknote",       cmp: Banknote },
  //   Security / safety
  { name: "Shield",         cmp: Shield },
  { name: "ShieldCheck",    cmp: ShieldCheck },
  { name: "ShieldAlert",    cmp: ShieldAlert },
  { name: "Lock",           cmp: Lock },
  { name: "Unlock",         cmp: Unlock },
  { name: "Key",            cmp: Key },
  { name: "KeyRound",       cmp: KeyRound },
  { name: "Fingerprint",    cmp: Fingerprint },
  { name: "AlertTriangle",  cmp: AlertTriangle },
  { name: "AlertCircle",    cmp: AlertCircle },
  { name: "Info",           cmp: Info },
  { name: "HelpCircle",     cmp: HelpCircle },
  //   Comms / tech
  { name: "Wifi",           cmp: Wifi },
  { name: "WifiOff",        cmp: WifiOff },
  { name: "Signal",         cmp: Signal },
  { name: "SignalHigh",     cmp: SignalHigh },
  { name: "SignalLow",      cmp: SignalLow },
  { name: "Bluetooth",      cmp: Bluetooth },
  { name: "Battery",        cmp: Battery },
  { name: "BatteryLow",     cmp: BatteryLow },
  { name: "Phone",          cmp: Phone },
  { name: "PhoneCall",      cmp: PhoneCall },
  { name: "Smartphone",     cmp: Smartphone },
  { name: "Tablet",         cmp: Tablet },
  { name: "Laptop",         cmp: Laptop },
  { name: "Monitor",        cmp: Monitor },
  { name: "Video",          cmp: Video },
  { name: "VideoOff",       cmp: VideoOff },
  //   Time / calendar
  { name: "Clock",          cmp: Clock },
  { name: "Timer",          cmp: Timer },
  { name: "Watch",          cmp: Watch },
  { name: "Calendar",       cmp: Calendar },
  { name: "CalendarDays",   cmp: CalendarDays },
  { name: "CalendarClock",  cmp: CalendarClock },
  { name: "CalendarCheck",  cmp: CalendarCheck },
  { name: "CalendarX",      cmp: CalendarX },
  { name: "Hourglass",      cmp: Hourglass },
  //   Docs / legal
  { name: "FileCheck",      cmp: FileCheck },
  { name: "FileWarning",    cmp: FileWarning },
  { name: "FileX",          cmp: FileX },
  { name: "ScrollText",     cmp: ScrollText },
  { name: "Stamp",          cmp: Stamp },
  { name: "PenTool",        cmp: PenTool },
  { name: "Signature",      cmp: Signature },
  { name: "Scale",          cmp: Scale },
  { name: "Gavel",          cmp: Gavel },
  //   Inclusion / holidays
  { name: "Flag",           cmp: Flag },
  { name: "GraduationCap",  cmp: GraduationCap },
  //   Eco
  { name: "Recycle",        cmp: Recycle },
  //   Retail
  { name: "Store",          cmp: Store },
  { name: "ShoppingBasket", cmp: ShoppingBasket },
  { name: "PackageCheck",   cmp: PackageCheck },
  { name: "PackageOpen",    cmp: PackageOpen },
  { name: "Tags",           cmp: Tags },
  //   Emotions / reactions
  { name: "Smile",          cmp: Smile },
  { name: "Frown",          cmp: Frown },
  { name: "Meh",            cmp: Meh },
  { name: "Laugh",          cmp: Laugh },
  { name: "Angry",          cmp: Angry },
  { name: "ThumbsUp",       cmp: ThumbsUp },
  { name: "ThumbsDown",     cmp: ThumbsDown },

  // ── Msg 607 · Expansion batch. Names verified against
  // lucide-react at write-time. Additive only. Total after this
  // batch: ~400 (Lucide) + 4 (custom SVGs) = ~404. Grew from 263
  // to ~400 due to Lucide package coverage limits — user-requested
  // icons that Lucide simply doesn't ship (Yacht, Ferry, Boombox,
  // Snorkel, Coconut, etc.) are skipped silently and listed in the
  // report.
  //   ── Music / audio / entertainment (25)
  { name: "Piano",           cmp: Piano },
  { name: "Guitar",          cmp: Guitar },
  { name: "Drum",            cmp: Drum },
  { name: "Speaker",         cmp: Speaker },
  { name: "AudioLines",      cmp: AudioLines },
  { name: "AudioWaveform",   cmp: AudioWaveform },
  { name: "Podcast",         cmp: Podcast },
  { name: "Clapperboard",    cmp: Clapperboard },
  { name: "BookmarkPlus",    cmp: BookmarkPlus },
  { name: "Play",            cmp: Play },
  { name: "Pause",           cmp: Pause },
  { name: "Disc",            cmp: Disc },
  { name: "Disc3",           cmp: Disc3 },
  { name: "Library",         cmp: Library },
  { name: "Newspaper",       cmp: Newspaper },
  { name: "Puzzle",          cmp: Puzzle },
  { name: "Dice1",           cmp: Dice1 },
  { name: "Dice3",           cmp: Dice3 },
  { name: "Dice5",           cmp: Dice5 },
  { name: "MedalIcon",       cmp: MedalIcon },
  { name: "Palette",         cmp: Palette },
  { name: "Brush",           cmp: Brush },
  { name: "Paintbrush",      cmp: Paintbrush },
  //   ── Cruise / ships / nautical (4)
  { name: "Radar",           cmp: Radar },
  { name: "Bath",            cmp: Bath },
  { name: "ShowerHead",      cmp: ShowerHead },
  { name: "Sofa",            cmp: Sofa },
  //   ── Adventure / wildlife (4)
  { name: "Snail",           cmp: Snail },
  { name: "Rat",             cmp: Rat },
  { name: "Worm",            cmp: Worm },
  { name: "Origami",         cmp: Origami },
  //   ── Weather (expanded, 8)
  { name: "CloudDrizzle",    cmp: CloudDrizzle },
  { name: "CloudFog",        cmp: CloudFog },
  { name: "CloudHail",       cmp: CloudHail },
  { name: "CloudLightning",  cmp: CloudLightning },
  { name: "CloudMoonRain",   cmp: CloudMoonRain },
  { name: "CloudSunRain",    cmp: CloudSunRain },
  { name: "CloudRainWind",   cmp: CloudRainWind },
  { name: "CloudOff",        cmp: CloudOff },
  //   ── Food / drink expanded (8)
  { name: "Apple",           cmp: Apple },
  { name: "Carrot",          cmp: Carrot },
  { name: "Nut",             cmp: Nut },
  { name: "Popsicle",        cmp: Popsicle },
  { name: "Donut",           cmp: Donut },
  { name: "CupSoda",         cmp: CupSoda },
  { name: "Wheat",           cmp: Wheat },
  { name: "HandPlatter",     cmp: HandPlatter },
  //   ── Transport expanded (9)
  { name: "CarFront",        cmp: CarFront },
  { name: "TramFront",       cmp: TramFront },
  { name: "Ambulance",       cmp: Ambulance },
  { name: "Navigation",      cmp: Navigation },
  { name: "NavigationOff",   cmp: NavigationOff },
  { name: "ParkingSquare",   cmp: ParkingSquare },
  { name: "ParkingCircle",   cmp: ParkingCircle },
  { name: "TicketCheck",     cmp: TicketCheck },
  { name: "TicketPercent",   cmp: TicketPercent },
  //   ── Money (3)
  { name: "CircleDollarSign", cmp: CircleDollarSign },
  { name: "HandCoins",       cmp: HandCoins },
  { name: "ReceiptText",     cmp: ReceiptText },
  //   ── Communication (16)
  { name: "PhoneMissed",     cmp: PhoneMissed },
  { name: "PhoneOff",        cmp: PhoneOff },
  { name: "PhoneOutgoing",   cmp: PhoneOutgoing },
  { name: "PhoneIncoming",   cmp: PhoneIncoming },
  { name: "MessagesSquare",  cmp: MessagesSquare },
  { name: "MailOpen",        cmp: MailOpen },
  { name: "MailPlus",        cmp: MailPlus },
  { name: "Mails",           cmp: Mails },
  { name: "Send",            cmp: Send },
  { name: "Inbox",           cmp: Inbox },
  { name: "Bell",            cmp: Bell },
  { name: "BellOff",         cmp: BellOff },
  { name: "BellRing",        cmp: BellRing },
  { name: "Megaphone",       cmp: Megaphone },
  { name: "Rss",             cmp: Rss },
  //   ── Docs / admin (9)
  { name: "Files",           cmp: Files },
  { name: "FilePlus",        cmp: FilePlus },
  { name: "FileMinus",       cmp: FileMinus },
  { name: "ClipboardCheck",  cmp: ClipboardCheck },
  { name: "Clipboard",       cmp: Clipboard },
  { name: "Contact",         cmp: Contact },
  { name: "ScrollIcon",      cmp: ScrollIcon },
  { name: "ArchiveIcon",     cmp: ArchiveIcon },
  { name: "ArchiveRestore",  cmp: ArchiveRestore },
  //   ── UI / generic (21)
  { name: "StarHalf",        cmp: StarHalf },
  { name: "Pin",             cmp: Pin },
  { name: "PinOff",          cmp: PinOff },
  { name: "X",               cmp: X },
  { name: "Check",           cmp: Check },
  { name: "Plus",            cmp: Plus },
  { name: "Minus",           cmp: Minus },
  { name: "Search",          cmp: Search },
  { name: "SlidersHorizontal", cmp: SlidersHorizontal },
  { name: "Filter",          cmp: Filter },
  { name: "Sparkle",         cmp: Sparkle },
  { name: "MoreHorizontal",  cmp: MoreHorizontal },
  { name: "MoreVertical",    cmp: MoreVertical },
  { name: "ArrowUp",         cmp: ArrowUp },
  { name: "ArrowDown",       cmp: ArrowDown },
  { name: "ArrowLeft",       cmp: ArrowLeft },
  { name: "ArrowRight",      cmp: ArrowRight },
  { name: "ChevronUp",       cmp: ChevronUp },
  { name: "ChevronDown",     cmp: ChevronDown },
  //   ── Time / calendar (6)
  { name: "AlarmClock",      cmp: AlarmClock },
  { name: "AlarmClockOff",   cmp: AlarmClockOff },
  { name: "CalendarPlus",    cmp: CalendarPlus },
  { name: "CalendarMinus",   cmp: CalendarMinus },
  { name: "CalendarRange",   cmp: CalendarRange },
  { name: "CalendarSearch",  cmp: CalendarSearch },
  //   ── Lodging / house (5)
  { name: "DoorOpen",        cmp: DoorOpen },
  { name: "DoorClosed",      cmp: DoorClosed },
  { name: "Lamp",            cmp: Lamp },
  { name: "WashingMachine",  cmp: WashingMachine },
  { name: "Boxes",           cmp: Boxes },
  { name: "Container",       cmp: Container },
  //   ── People (5)
  { name: "User2",           cmp: User2 },
  { name: "UserCheck",       cmp: UserCheck },
  { name: "UserPlus",        cmp: UserPlus },
  { name: "UserMinus",       cmp: UserMinus },
  { name: "UserX",           cmp: UserX },
  //   ── Misc
  { name: "Gem",             cmp: Gem },
  //   ── Business / analytics (10)
  { name: "BarChart",        cmp: BarChart },
  { name: "BarChart2",       cmp: BarChart2 },
  { name: "BarChart3",       cmp: BarChart3 },
  { name: "LineChart",       cmp: LineChart },
  { name: "PieChart",        cmp: PieChart },
  { name: "TrendingUp",      cmp: TrendingUp },
  { name: "TrendingDown",    cmp: TrendingDown },
  { name: "Layers",          cmp: Layers },
  { name: "Layers2",         cmp: Layers2 },
  { name: "Layers3",         cmp: Layers3 },
  //   ── Msg 607 · Part 2 · 50 additional icons
  { name: "ChefHat",         cmp: ChefHat },
  { name: "Fence",           cmp: Fence },
  { name: "StickyNote",      cmp: StickyNote },
  { name: "NotebookPen",     cmp: NotebookPen },
  { name: "Notebook",        cmp: Notebook },
  { name: "NotebookText",    cmp: NotebookText },
  { name: "PencilRuler",     cmp: PencilRuler },
  { name: "Ruler",           cmp: Ruler },
  { name: "Highlighter",     cmp: Highlighter },
  { name: "Bold",            cmp: Bold },
  { name: "Italic",          cmp: Italic },
  { name: "Underline",       cmp: Underline },
  { name: "BatteryCharging", cmp: BatteryCharging },
  { name: "BatteryFull",     cmp: BatteryFull },
  { name: "BatteryMedium",   cmp: BatteryMedium },
  { name: "Plug",            cmp: Plug },
  { name: "PlugZap",         cmp: PlugZap },
  { name: "Power",           cmp: Power },
  { name: "Lightbulb",       cmp: Lightbulb },
  { name: "Tv",              cmp: Tv },
  { name: "Tv2",             cmp: Tv2 },
  { name: "Airplay",         cmp: Airplay },
  { name: "GitBranch",       cmp: GitBranch },
  { name: "GitCommit",       cmp: GitCommit },
  { name: "GitFork",         cmp: GitFork },
  { name: "Github",          cmp: Github },
  { name: "Chrome",          cmp: Chrome },
  { name: "MousePointer",    cmp: MousePointer },
  { name: "QrCode",          cmp: QrCode },
  { name: "Barcode",         cmp: Barcode },
  { name: "ScanLine",        cmp: ScanLine },
  { name: "Scan",            cmp: Scan },
  { name: "Focus",           cmp: Focus },
  { name: "Aperture",        cmp: Aperture },
  { name: "Image",           cmp: Image },
  { name: "ImagePlus",       cmp: ImagePlus },
  { name: "ImageDown",       cmp: ImageDown },
  { name: "PackagePlus",     cmp: PackagePlus },
  { name: "PackageMinus",    cmp: PackageMinus },
  { name: "PackageSearch",   cmp: PackageSearch },
  { name: "PackageX",        cmp: PackageX },
  { name: "Pipette",         cmp: Pipette },
  { name: "TestTube",        cmp: TestTube },
  { name: "Beaker",          cmp: Beaker },
  { name: "Atom",            cmp: Atom },
  { name: "Sigma",           cmp: Sigma },
  { name: "Anvil",           cmp: Anvil },
  { name: "Axe",             cmp: Axe },
  { name: "Sword",           cmp: Sword },
  { name: "Swords",          cmp: Swords },
  { name: "FlaskRound",      cmp: FlaskRound },
  { name: "Milestone",       cmp: Milestone },
  //   ── Msg 607 · Part 3 · final push toward 500 (~55 icons)
  { name: "Wallet2",         cmp: Wallet2 },
  { name: "School",          cmp: School },
  { name: "Hospital",        cmp: Hospital },
  { name: "SunMedium",       cmp: SunMedium },
  { name: "SunDim",          cmp: SunDim },
  { name: "MoonStar",        cmp: MoonStar },
  { name: "AlertOctagon",    cmp: AlertOctagon },
  { name: "Save",            cmp: Save },
  { name: "Trash",           cmp: Trash },
  { name: "Trash2",          cmp: Trash2 },
  { name: "Undo",            cmp: Undo },
  { name: "Undo2",           cmp: Undo2 },
  { name: "Redo",            cmp: Redo },
  { name: "Redo2",           cmp: Redo2 },
  { name: "ClipboardCopy",   cmp: ClipboardCopy },
  { name: "Share",           cmp: Share },
  { name: "Share2",          cmp: Share2 },
  { name: "ExternalLink",    cmp: ExternalLink },
  { name: "Link2",           cmp: Link2 },
  { name: "Unlink",          cmp: Unlink },
  { name: "BookMarked",      cmp: BookMarked },
  { name: "BookText",        cmp: BookText },
  { name: "Users2",          cmp: Users2 },
  { name: "Target",          cmp: Target },
  { name: "Crosshair",       cmp: Crosshair },
  { name: "Locate",          cmp: Locate },
  { name: "LocateFixed",     cmp: LocateFixed },
  { name: "Antenna",         cmp: Antenna },
  { name: "CircleUser",      cmp: CircleUser },
  { name: "CircleUserRound", cmp: CircleUserRound },
  { name: "CircleAlert",     cmp: CircleAlert },
  { name: "CircleCheck",     cmp: CircleCheck },
  { name: "CircleX",         cmp: CircleX },
  { name: "SmilePlus",       cmp: SmilePlus },
  { name: "FlagTriangleRight", cmp: FlagTriangleRight },
  { name: "FlagTriangleLeft",  cmp: FlagTriangleLeft },
  { name: "TentTree",        cmp: TentTree },
  { name: "Gauge",           cmp: Gauge },
  { name: "MapIcon",         cmp: MapIcon },
  { name: "Split",           cmp: Split },
  { name: "Merge",           cmp: Merge },
  { name: "ArrowLeftRight",  cmp: ArrowLeftRight },
  { name: "ArrowUpDown",     cmp: ArrowUpDown },
  { name: "SortAsc",         cmp: SortAsc },
  { name: "SortDesc",        cmp: SortDesc },
  { name: "Rows",            cmp: Rows },
  { name: "Columns",         cmp: Columns },
  { name: "Grid3x3",         cmp: Grid3x3 },
  { name: "LayoutGrid",      cmp: LayoutGrid },
  { name: "LayoutList",      cmp: LayoutList },
  { name: "LayoutDashboard", cmp: LayoutDashboard },
  { name: "Kanban",          cmp: Kanban },
  { name: "KanbanSquare",    cmp: KanbanSquare },
  { name: "ListChecks",      cmp: ListChecks },
  { name: "ListTodo",        cmp: ListTodo },
  { name: "ListPlus",        cmp: ListPlus },
  { name: "ListMinus",       cmp: ListMinus },
  { name: "ListX",           cmp: ListX },
  { name: "SlidersVertical", cmp: SlidersVertical },
  { name: "Wrench",          cmp: Wrench },
  { name: "Hammer",          cmp: Hammer },
  { name: "Cog",             cmp: Cog },
  { name: "ArrowUpRight",    cmp: ArrowUpRight },
  { name: "ArrowDownRight",  cmp: ArrowDownRight },
  { name: "ArrowUpLeft",     cmp: ArrowUpLeft },
  { name: "ArrowDownLeft",   cmp: ArrowDownLeft },

  // ── Msg 595 — custom SVGs that Lucide doesn't ship.
  { name: "Beach",          cmp: Beach },
  { name: "Wheelchair",     cmp: Wheelchair },
  { name: "Passport",       cmp: Passport },
  { name: "Volcano",        cmp: Volcano },
];


// Fast lookup: iconName → component.
export const ICON_BY_NAME = Object.freeze(
  Object.fromEntries(CURATED_ICONS.map(({ name, cmp }) => [name, cmp]))
);


// Resolve an icon name to a component. Always safe — returns Folder
// when the name is unknown or blank so previously-saved category
// icons that were later removed from the picker never render as
// broken.
export function iconByName(name) {
  if (!name) return Folder;
  return ICON_BY_NAME[name] || Folder;
}
